/* 三國逗陣打 · 武將篩選、排序、搜尋（v0.3.0）：選將畫面與「角色」分頁共用
 * 每個列表有自己的狀態（{ fac, cls, role, q, sort }），記在 localStorage，下次打開照舊。 */
import { SG } from '../core/sg.ts';

export interface HeroFilter { fac: string; cls: string; role: string; q: string; sort: string }
const FAC: [string, string][] = [['all', '全部'], ['shu', '蜀'], ['wu', '吳'], ['wei', '魏']];
const CLS: [string, string][] = [['all', '全部'], ['int', '智將'], ['str', '武將'], ['agi', '敏將']];
const SORTS: [string, string][] = [['def', '預設'], ['mastery', '熟練度'], ['win', '勝率'], ['games', '場次'], ['name', '名稱']];
const ROLES = () => Object.keys(SG.ROLES);
const mainOf = (h: any) => h.attr || (SG.ROLES[h.statRole || h.role] || SG.ROLES['戰士']).main;

export function loadFilter(key: string): HeroFilter {
  const d: HeroFilter = { fac: 'all', cls: 'all', role: 'all', q: '', sort: 'def' };
  try { Object.assign(d, JSON.parse(localStorage.getItem('sankuo-hf-' + key) || '{}')); } catch (e) { /* 沒有就用預設 */ }
  d.q = ''; // 搜尋字不保留
  return d;
}
function saveFilter(key: string, f: HeroFilter) { try { localStorage.setItem('sankuo-hf-' + key, JSON.stringify(Object.assign({}, f, { q: '' }))); } catch (e) { /* 忽略 */ } }

// 搜尋：名字、字（sub）、定位、分類、兵器、陣營、技能名（含每個形態的）都比對；定位篩選主、副定位都算（戰士／射手兩邊都會出現）
const hay = (h: any) => [h.name, h.sub, h.role, h.roleText, h.weaponName, SG.classOf(h), { shu: '蜀', wu: '吳', wei: '魏' }[h.faction as string], ...(SG.skillRows ? SG.skillRows(h).map((r: any) => r.s.name) : (h.skills || []).map((s: any) => s.name))].filter(Boolean).join(' ').toLowerCase();

export function applyFilter(f: HeroFilter, list: any[] = SG.HEROES): any[] {
  const P = SG.Meta.profile, q = f.q.trim().toLowerCase();
  const rec = (h: any) => P.heroes[h.id] || { xp: 0, games: 0, wins: 0 };
  const out = list.filter((h) => (f.fac === 'all' || h.faction === f.fac) && (f.cls === 'all' || mainOf(h) === f.cls) && (f.role === 'all' || h.role === f.role || h.role2 === f.role) && (!q || hay(h).includes(q)));
  const idx = new Map(SG.HEROES.map((h: any, i: number) => [h.id, i]));
  const by: Record<string, (a: any, b: any) => number> = {
    def: (a, b) => (idx.get(a.id) as number) - (idx.get(b.id) as number),
    mastery: (a, b) => rec(b).xp - rec(a).xp,
    // 勝率：沒打過的排最後；同勝率場次多的在前
    win: (a, b) => { const ra = rec(a), rb = rec(b), wa = ra.games ? ra.wins / ra.games : -1, wb = rb.games ? rb.wins / rb.games : -1; return wb - wa || rb.games - ra.games; },
    games: (a, b) => rec(b).games - rec(a).games,
    name: (a, b) => a.name.localeCompare(b.name, 'zh-Hant'),
  };
  return out.sort((a, b) => (by[f.sort] || by.def)(a, b) || by.def(a, b));
}

// 篩選列：陣營、分類、定位、排序、搜尋
export function filterBar(id: string, f: HeroFilter): string {
  const seg = (k: string, opts: [string, string][], label: string) => `<span class="hf-lb">${label}</span><div class="seg" role="group" aria-label="${label}" data-hk="${k}">${opts.map(([v, t]) => `<button class="btn" type="button" data-v="${v}" aria-pressed="${(f as any)[k] === v}">${t}</button>`).join('')}</div>`;
  return `<div class="hfbar" id="${id}">
    ${seg('fac', FAC, '陣營')}${seg('cls', CLS, '分類')}
    <label class="hf-sel">定位 <select data-hk="role"><option value="all">全部</option>${ROLES().map((r) => `<option value="${r}"${f.role === r ? ' selected' : ''}>${r}</option>`).join('')}</select></label>
    <label class="hf-sel">排序 <select data-hk="sort">${SORTS.map(([v, t]) => `<option value="${v}"${f.sort === v ? ' selected' : ''}>${t}</option>`).join('')}</select></label>
    <input class="hf-q" type="search" data-hk="q" placeholder="搜尋武將、技能…" value="${f.q.replace(/"/g, '&quot;')}" aria-label="搜尋武將">
    <span class="hf-n" aria-live="polite"></span></div>`;
}

// 綁定：任何變動都存起來並呼叫 redraw（搜尋框打字時不重畫篩選列，避免游標跳掉）
export function bindFilter(box: HTMLElement, key: string, f: HeroFilter, redraw: () => void): void {
  box.querySelectorAll<HTMLElement>('.seg[data-hk]').forEach((g) => {
    g.onclick = (e) => {
      const b = (e.target as HTMLElement).closest('button'); if (!b) return;
      (f as any)[g.dataset.hk as string] = b.dataset.v;
      g.querySelectorAll('button').forEach((x) => x.setAttribute('aria-pressed', String(x === b)));
      saveFilter(key, f); redraw();
    };
  });
  box.querySelectorAll<HTMLSelectElement>('select[data-hk]').forEach((s) => { s.onchange = () => { (f as any)[s.dataset.hk as string] = s.value; saveFilter(key, f); redraw(); }; });
  const q = box.querySelector<HTMLInputElement>('.hf-q');
  if (q) q.oninput = () => { f.q = q.value; redraw(); };
}
export function setCount(box: HTMLElement, n: number): void { const e = box.querySelector('.hf-n'); if (e) e.textContent = n === SG.HEROES.length ? '' : `${n} 名`; }

SG.HeroFilter = { loadFilter, applyFilter, filterBar, bindFilter, setCount };
